import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Store, FileText } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from 'react-i18next';

interface ExportOptionsDialogProps {
  projectId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onExport: (options: ExportOptions) => void;
}

export interface ExportOptions {
  includeBudget: boolean;
  selectedVendor: string | null;
  includeCamera: boolean;
  includeGE: boolean;
}

interface Vendor {
  id: string;
  name: string;
}

export function ExportOptionsDialog({ 
  projectId, 
  isOpen, 
  onOpenChange, 
  onExport 
}: ExportOptionsDialogProps) {
  const { t } = useTranslation();
  const [options, setOptions] = useState<ExportOptions>({
    includeBudget: true,
    selectedVendor: null,
    includeCamera: true,
    includeGE: true,
  });
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<string | null>(null);

  // Fetch vendors that are actually used in the project's gear items
  useEffect(() => {
    const fetchVendors = async () => {
      const { data, error } = await supabase
        .from('gear_items')
        .select(`
          vendor_id,
          vendors (
            id,
            name
          )
        `)
        .eq('project_id', projectId)
        .not('vendor_id', 'is', null);

      if (error) {
        toast.error(t('export.failed_load_vendors'));
        return;
      }

      // Filter out null vendors and create a unique list
      const uniqueVendors = data
        .map(item => item.vendors)
        .filter((v): v is NonNullable<typeof v> => v !== null)
        .reduce((acc: Vendor[], vendor) => {
          if (!acc.some(v => v.id === vendor.id)) {
            acc.push(vendor);
          }
          return acc;
        }, []);

      setVendors(uniqueVendors);
    };

    if (isOpen) {
      fetchVendors();
    }
  }, [projectId, isOpen, t]);

  const handleNormalExport = () => {
    if (!options.includeCamera && !options.includeGE) {
      toast.error(t('export.select_at_least_one_section'));
      return;
    }
    onExport({
      ...options,
      selectedVendor: null
    });
  };

  const handleVendorExport = () => {
    if (!selectedVendor) {
      toast.error(t('export.select_vendor'));
      return;
    }
    if (!options.includeCamera && !options.includeGE) {
      toast.error(t('export.select_at_least_one_section'));
      return;
    }
    onExport({
      ...options,
      selectedVendor
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('export.options_title')}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="includeBudget"
              checked={options.includeBudget}
              onCheckedChange={(checked) => 
                setOptions(prev => ({ ...prev, includeBudget: checked as boolean }))
              }
            />
            <Label htmlFor="includeBudget">{t('export.include_budget')}</Label>
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeCamera"
                checked={options.includeCamera}
                onCheckedChange={(checked) => 
                  setOptions(prev => ({ ...prev, includeCamera: checked as boolean }))
                }
              />
              <Label htmlFor="includeCamera">{t('export.include_camera')}</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeGE"
                checked={options.includeGE}
                onCheckedChange={(checked) => 
                  setOptions(prev => ({ ...prev, includeGE: checked as boolean }))
                }
              />
              <Label htmlFor="includeGE">{t('export.include_ge')}</Label>
            </div>
          </div>

          {vendors.length > 0 && (
            <>
              <Separator className="my-4" />
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t('export.export_by_vendor')}</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  {t('export.select_vendor_description')}
                </p>
                <Select
                  value={selectedVendor || ""}
                  onValueChange={setSelectedVendor}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('export.select_vendor_placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((vendor) => (
                      <SelectItem key={vendor.id} value={vendor.id}>
                        <div className="flex items-center gap-2">
                          <Store className="h-4 w-4" />
                          <span>{vendor.name}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex justify-center gap-4">
          <Button 
            onClick={handleNormalExport} 
            className="gap-2"
          >
            <FileText className="h-4 w-4" />
            {t('export.generate_full_pdf')}
          </Button>
          {vendors.length > 0 && (
            <Button 
              onClick={handleVendorExport} 
              className="gap-2"
              variant="secondary"
            >
              <Store className="h-4 w-4" />
              {t('export.generate_vendor_pdf')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
